// 内容服务 - 从后端API获取配置内容

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || '/api';
const SERVER_BASE_URL = process.env.VUE_APP_SERVER_BASE_URL || ''; // 服务器基础URL，用于图片路径前缀
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * 统一前端语言代码（兼容历史值）
 * @param {string} locale
 * @returns {'zh'|'zh-Hant'|'en'}
 */
export function normalizeLocale(locale = 'zh') {
  if (!locale || typeof locale !== 'string') return 'zh';
  const value = locale.trim();
  const lower = value.toLowerCase();

  if (value === 'zh-Hant' || lower === 'zh-hant' || lower === 'zh-tw' || lower === 'zh_hant' || lower === 'tw') {
    return 'zh-Hant';
  }
  if (lower === 'en' || lower.startsWith('en-') || lower.startsWith('en_')) {
    return 'en';
  }
  if (lower === 'zh' || lower === 'cn' || lower === 'zh-cn' || lower === 'zh_cn' || lower.startsWith('zh-')) {
    return 'zh';
  }
  return 'zh';
}

/**
 * 将前端语言代码转换为后端接口语言代码
 * @param {string} locale
 * @returns {'cn'|'tw'|'en'}
 */
export function localeToApiLang(locale = 'zh') {
  const normalized = normalizeLocale(locale);
  if (normalized === 'zh-Hant') return 'tw';
  if (normalized === 'en') return 'en';
  return 'cn';
}

function toApiLang(lang = 'cn') {
  if (lang === 'cn' || lang === 'tw' || lang === 'en') return lang;
  return localeToApiLang(lang);
}

function resolveServerBaseUrl() {
  const normalized = (SERVER_BASE_URL || '').replace(/\/+$/, '');
  if (!normalized) return '';

  // 生产环境兜底：如果误配置为 localhost，且当前并非本机访问，则不添加前缀，避免图片全部指向用户本机
  if (!isDevelopment && typeof window !== 'undefined') {
    const currentHost = window.location.hostname;
    const isCurrentLocal = currentHost === 'localhost' || currentHost === '127.0.0.1';
    const isConfiguredLocal = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(normalized);
    if (!isCurrentLocal && isConfiguredLocal) {
      return '';
    }
  }

  return normalized;
}

const RESOLVED_SERVER_BASE_URL = resolveServerBaseUrl();

/**
 * 为图片URL添加服务器前缀（如果需要）
 * @param {string} url - 图片URL
 * @returns {string} 处理后的图片URL
 */
export function addServerPrefix(url) {
  if (!url) return '';
  // 如果已经是完整URL（http/https），直接返回
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // 如果是 /assets/ 开头的图片，使用前端静态资源，不加前缀
  if (url.startsWith('/assets/')) {
    return url;
  }
  // 开发环境中，/uploads/ 等路径已被 Vue 代理处理，不需要加前缀
  if (isDevelopment && (url.startsWith('/uploads/') || url.startsWith('/uploads'))) {
    return url;
  }
  // 生产环境中，为非 assets 路径添加后端服务器前缀
  if (url.startsWith('/') && RESOLVED_SERVER_BASE_URL && !isDevelopment) {
    return RESOLVED_SERVER_BASE_URL + url;
  }
  return url;
}


/**
 * 获取指定分类的所有内容
 * @param {string} category - 分类名称 (home, nav, footer等)
 * @param {string} lang - 语言 (cn, en, tw)
 * @returns {Promise<Object>} 内容对象，key-value格式
 */
export async function getContentsByCategory(category, lang = 'cn') {
  try {
    const apiLang = toApiLang(lang);
    const response = await fetch(`${API_BASE_URL}/public/contents/category/${category}?lang=${apiLang}`);
    const data = await response.json();

    if (data.code === 0) {
      const contents = data.data || [];

      // 将数组格式转换为对象格式以保持向后兼容
      const result = {};
      if (Array.isArray(contents)) {
        contents.forEach(item => {
          if (item.type === 'route') {
            // 路由类型：包含文本和路由的对象
            result[item.key] = {
              text: item.value,
              route: item.route,
              route_type: item.route_type
            };
          } else {
            // 其他类型：直接值
            result[item.key] = item.value;
          }
        });
      }

      return result;
    } else {
      console.error('获取内容失败:', data.message);
      return {};
    }
  } catch (error) {
    console.error('获取内容失败:', error);
    return {};
  }
}

/**
 * 获取单个内容项
 * @param {string} key - 内容key
 * @param {string} lang - 语言 (cn, en, tw)
 * @returns {Promise<string>} 内容值
 */
export async function getContentByKey(key, lang = 'cn') {
  try {
    const apiLang = toApiLang(lang);
    const response = await fetch(`${API_BASE_URL}/public/contents/key/${key}?lang=${apiLang}`);
    const data = await response.json();

    if (data.code === 0) {
      return data.data?.value || '';
    } else {
      console.error('获取内容失败:', data.message);
      return '';
    }
  } catch (error) {
    console.error('获取内容失败:', error);
    return '';
  }
}

/**
 * 获取图片资源路径
 * @param {string} key - 图片key
 * @returns {Promise<string>} 图片路径
 */
export async function getImageUrl(key) {
  try {
    const response = await fetch(`${API_BASE_URL}/public/contents/key/${key}`);
    const data = await response.json();

    if (data.code === 0) {
      return data.data?.value || '';
    } else {
      console.error('获取图片失败:', data.message);
      return '';
    }
  } catch (error) {
    console.error('获取图片失败:', error);
    return '';
  }
}

/**
 * 获取链接地址
 * @param {string} key - 链接key
 * @returns {Promise<string>} 链接地址
 */
export async function getLinkUrl(key) {
  try {
    const response = await fetch(`${API_BASE_URL}/public/contents/key/${key}`);
    const data = await response.json();

    if (data.code === 0) {
      return data.data?.value || '';
    } else {
      console.error('获取链接失败:', data.message);
      return '#';
    }
  } catch (error) {
    console.error('获取链接失败:', error);
    return '#';
  }
}

/**
 * 获取路由地址
 * @param {string} key - 路由key
 * @returns {Promise<string>} 路由地址
 */
export async function getRouteUrl(key) {
  try {
    const response = await fetch(`${API_BASE_URL}/public/contents/key/${key}`);
    const data = await response.json();

    if (data.code === 0) {
      return data.data?.value || '#';
    } else {
      console.error('获取路由失败:', data.message);
      return '#';
    }
  } catch (error) {
    console.error('获取路由失败:', error);
    return '#';
  }
}

/**
 * 获取内容列表
 * @param {string} category - 分类名称（可选）
 * @param {string} lang - 语言 (cn, en, tw)
 * @returns {Promise<Array>} 内容列表
 */
export async function getContentList(category = '', lang = 'cn') {
  try {
    const apiLang = toApiLang(lang);
    const params = new URLSearchParams({ lang: apiLang, page: 1, page_size: 1000 });
    if (category) {
      params.append('category', category);
    }

    const response = await fetch(`${API_BASE_URL}/public/contents?${params}`);
    const data = await response.json();

    if (data.code === 0) {
      return data.data?.list || [];
    } else {
      console.error('获取内容列表失败:', data.message);
      return [];
    }
  } catch (error) {
    console.error('获取内容列表失败:', error);
    return [];
  }
}

/**
 * 将内容列表转换为key-value对象
 * @param {Array} contentList - 内容列表
 * @returns {Object} key-value对象
 */
export function contentListToObject(contentList) {
  const result = {};
  contentList.forEach(item => {
    result[item.key] = item.value;
  });
  return result;
}

/**
 * 获取帮助页面数据
 * @param {string} lang - 语言 (cn, en, tw)
 * @returns {Promise<Object>} 帮助页面数据
 */
export async function getHelpPageData(lang = 'cn') {
  try {
    const apiLang = toApiLang(lang);
    const response = await fetch(`${API_BASE_URL}/public/help/page?lang=${apiLang}`);
    const data = await response.json();

    if (data.code === 0) {
      return data.data || {};
    } else {
      console.error('获取帮助页面数据失败:', data.message);
      return {};
    }
  } catch (error) {
    console.error('获取帮助页面数据失败:', error);
    return {};
  }
}

/**
 * 获取问题详情
 * @param {number} questionId - 问题ID
 * @param {string} lang - 语言 (cn, en, tw)
 * @returns {Promise<Object>} 问题详情数据
 */
export async function getQuestionDetail(questionId, lang = 'cn') {
  try {
    const apiLang = toApiLang(lang);
    const response = await fetch(`${API_BASE_URL}/help/questions/${questionId}?lang=${apiLang}`);
    const data = await response.json();

    if (data.code === 0) {
      return data.data || {};
    } else {
      console.error('获取问题详情失败:', data.message);
      return {};
    }
  } catch (error) {
    console.error('获取问题详情失败:', error);
    return {};
  }
}

/**
 * 获取FAQ详情
 * @param {number} faqId - FAQ ID
 * @param {string} lang - 语言 (cn, en, tw)
 * @returns {Promise<Object>} FAQ详情数据
 */
export async function getFaqDetail(faqId, lang = 'cn') {
  try {
    const apiLang = toApiLang(lang);
    const response = await fetch(`${API_BASE_URL}/help/faqs/${faqId}?lang=${apiLang}`);
    const data = await response.json();

    if (data.code === 0) {
      return data.data || {};
    } else {
      console.error('获取FAQ详情失败:', data.message);
      return {};
    }
  } catch (error) {
    console.error('获取FAQ详情失败:', error);
    return {};
  }
}

/**
 * Vue混入：内容管理
 */
export const ContentMixin = {
  data() {
    return {
      contentData: {},
      imageUrls: {},
      linkUrls: {},
      routeUrls: {}
    };
  },
  methods: {
    /**
     * 获取内容
     * @param {string} key - 内容key
     * @param {string} defaultValue - 默认值
     * @returns {string} 内容值
     */
    getContent(key, defaultValue = '') {
      return this.contentData[key] || defaultValue;
    },

    /**
     * 获取图片URL
     * @param {string} key - 图片key
     * @param {string} defaultValue - 默认值
     * @returns {string} 图片URL
     */
    getImageUrl(key, defaultValue = '') {
      return this.imageUrls[key] || defaultValue;
    },

    /**
     * 获取链接URL
     * @param {string} key - 链接key
     * @param {string} defaultValue - 默认值
     * @returns {string} 链接URL
     */
    getLinkUrl(key, defaultValue = '#') {
      return this.linkUrls[key] || defaultValue;
    },

    /**
     * 获取路由URL
     * @param {string} key - 路由key
     * @param {string} defaultValue - 默认值
     * @returns {string} 路由URL
     */
    getRouteUrl(key, defaultValue = '#') {
      return this.routeUrls[key] || defaultValue;
    },

    /**
     * 加载分类内容
     * @param {string} category - 分类名称
     */
    async loadCategoryContent(category) {
      try {
        const apiLang = toApiLang(this.currentLang);
        // 获取所有分类的内容（包含类型信息）
        const response = await fetch(`${API_BASE_URL}/public/contents/category/${category}?lang=${apiLang}`);
        const responseData = await response.json();

        if (responseData.code === 0) {
          const contents = responseData.data || [];

          // 分别处理不同类型的项目
          const contentData = {};
          const imageUrls = {};
          const linkUrls = {};
          const routeUrls = {};

          contents.forEach(item => {
            if (item.type === 'image') {
              // 为图片URL添加服务器前缀
              imageUrls[item.key] = addServerPrefix(item.value);
            } else if (item.type === 'route') {
              routeUrls[item.key] = item.route || '#';
            } else if (item.type === 'download_link') {
              linkUrls[item.key] = item.value;
            } else {
              // text, richtext, json等其他类型
              contentData[item.key] = item.value;
            }
          });

          // 更新相应的数据对象
          this.contentData = { ...this.contentData, ...contentData };
          this.imageUrls = { ...this.imageUrls, ...imageUrls };
          this.linkUrls = { ...this.linkUrls, ...linkUrls };
          this.routeUrls = { ...this.routeUrls, ...routeUrls };
        } else {
          console.error('获取分类内容失败:', responseData.message);
        }
      } catch (error) {
        console.error('加载内容失败:', error);
      }
    },

    /**
     * 加载图片URL
     * @param {string} key - 图片key
     */
    async loadImage(key) {
      try {
        const url = await getImageUrl(key);
        // 为图片URL添加服务器前缀
        const prefixedUrl = addServerPrefix(url);
        this.$set(this.imageUrls, key, prefixedUrl);
        return prefixedUrl;
      } catch (error) {
        console.error('加载图片失败:', error);
        return '';
      }
    },

    /**
     * 加载链接URL
     * @param {string} key - 链接key
     */
    async loadLink(key) {
      try {
        const url = await getLinkUrl(key);
        this.$set(this.linkUrls, key, url);
        return url;
      } catch (error) {
        console.error('加载链接失败:', error);
        return '#';
      }
    },

    /**
     * 加载路由URL
     * @param {string} key - 路由key
     */
    async loadRoute(key) {
      try {
        const url = await getRouteUrl(key);
        this.$set(this.routeUrls, key, url);
        return url;
      } catch (error) {
        console.error('加载路由失败:', error);
        return '#';
      }
    }
  }
};
